import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Truck, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LogoMark, Wordmark } from "@/components/Logo";
import { SmsOptInCheckbox, TosCheckbox } from "@/components/Legal";
import api, { apiErrorMessage, setToken } from "@/lib/api";

export function Login() {
  const [form, setForm] = useState({ email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  // internal deep link the user was aiming for before the sign-in detour
  const nextParam = params.get("next");
  const safeNext = nextParam && nextParam.startsWith("/") && !nextParam.startsWith("//") ? nextParam : null;

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const sendReset = async (e) => {
    e.preventDefault();
    if (forgotLoading) return;
    setForgotLoading(true);
    try {
      await api.post("/auth/forgot-password", {
        email: forgotEmail.trim().toLowerCase(),
        origin_url: window.location.origin,
      });
      setForgotSent(true);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Couldn't send the reset email."));
    } finally {
      setForgotLoading(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const user = await login(form.email.trim().toLowerCase(), form.password);
      toast.success("Welcome back.");
      const dest = safeNext || (user.role === "driver" ? "/app/driver" : user.role === "admin" ? "/app/admin/users" : "/app");
      nav(dest, { replace: true });
    } catch (err) {
      toast.error(apiErrorMessage(err, "Login failed."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell title="Sign in" subtitle="Ship or drive — same account.">
      <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form" noValidate>
        <Input label="Email" name="email" type="email" value={form.email} onChange={onChange} placeholder="you@boatrvmoving.com" testid="login-email" autoComplete="email" />
        <Input label="Password" name="password" type="password" value={form.password} onChange={onChange} placeholder="••••••••" testid="login-password" autoComplete="current-password" withToggle />
        <div className="text-right -mt-2">
          <button
            type="button"
            onClick={() => {
              setForgotOpen((v) => !v);
              setForgotSent(false);
            }}
            data-testid="forgot-password-link"
            className="text-[11px] uppercase tracking-widest font-bold text-zinc-500 hover:text-[#FF3B30] transition-colors"
          >
            Forgot password?
          </button>
        </div>
        <SubmitButton loading={submitting} label="Sign in" testid="login-submit" />
      </form>
      {forgotOpen && (
        <div className="mt-5 border border-white/[0.08] bg-[#141416] p-5" data-testid="forgot-password-panel">
          {forgotSent ? (
            <p className="text-sm text-zinc-300" data-testid="forgot-success">
              If that email is registered, a reset link is on its way — it works once and expires in 15 minutes.
            </p>
          ) : (
            <form onSubmit={sendReset} className="space-y-3" data-testid="forgot-password-form" noValidate>
              <Input label="Account email" name="forgot_email" type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} placeholder="you@boatrvmoving.com" testid="forgot-email" autoComplete="email" />
              <SubmitButton loading={forgotLoading} label="Email me a reset link" testid="forgot-submit" />
            </form>
          )}
        </div>
      )}
      <p className="text-xs text-zinc-500 mt-6 text-center">
        No account?{" "}
        <Link to="/signup" className="text-[#FF3B30] hover:text-white underline underline-offset-2">
          Create one
        </Link>
      </p>
    </AuthShell>
  );
}

export function Signup() {
  const [params] = useSearchParams();
  const initialRole = params.get("role") === "driver" ? "driver" : "shipper";
  const initialRef = (params.get("ref") || "").toUpperCase();
  const [role, setRole] = useState(initialRole);
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", referral_code: initialRef });
  const [tosAccepted, setTosAccepted] = useState(false);
  const [smsOptIn, setSmsOptIn] = useState(false); // optional SMS consent — never pre-checked (CTIA)
  const [submitting, setSubmitting] = useState(false);
  const { register } = useAuth();
  const nav = useNavigate();

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (role === "driver") {
      // drivers register via the document-verified flow
      nav("/driver-signup");
      return;
    }
    if (form.password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    if (!tosAccepted) {
      toast.error("Please agree to the Terms and Conditions and Privacy Policy to continue.");
      return;
    }
    setSubmitting(true);
    try {
      const user = await register({
        email: form.email.trim().toLowerCase(),
        password: form.password,
        name: form.name.trim(),
        role,
        phone: form.phone.trim(),
        referral_code: form.referral_code.trim().toUpperCase(),
        tos_accepted: tosAccepted,
        sms_opt_in: smsOptIn,
      });
      toast.success(`Welcome, ${user.name}.`);
      nav(user.role === "driver" ? "/app/driver" : "/app", { replace: true });
    } catch (err) {
      toast.error(apiErrorMessage(err, "Registration failed."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell title="Create account" subtitle="90 seconds. No credit card.">
      <div className="grid grid-cols-2 gap-2 mb-5" data-testid="role-toggle">
        {[
          { v: "shipper", label: "I'm shipping", icon: ShieldCheck },
          { v: "driver", label: "I'm a driver", icon: Truck },
        ].map((opt) => {
          const Icon = opt.icon;
          const active = role === opt.v;
          return (
            <button
              key={opt.v}
              type="button"
              onClick={() => setRole(opt.v)}
              data-testid={`role-${opt.v}`}
              className={`flex items-center gap-2 py-3 px-4 border-2 text-xs uppercase tracking-widest font-bold transition-colors ${
                active
                  ? "bg-[#FF3B30] border-[#FF3B30] text-white"
                  : "border-white/10 text-zinc-400 hover:border-white/30 hover:text-white"
              }`}
            >
              <Icon className="w-4 h-4" />
              {opt.label}
            </button>
          );
        })}
      </div>

      <form onSubmit={onSubmit} className="space-y-4" data-testid="signup-form" noValidate>
        <Input label="Full Name" name="name" value={form.name} onChange={onChange} placeholder="Diesel Dan" testid="signup-name" autoComplete="name" />
        <Input label="Email" name="email" type="email" value={form.email} onChange={onChange} placeholder="dan@haulshop.com" testid="signup-email" autoComplete="email" />
        <Input label="Phone" name="phone" type="tel" value={form.phone} onChange={onChange} placeholder="(555) 010-9090" testid="signup-phone" autoComplete="tel" />
        <SmsOptInCheckbox checked={smsOptIn} onChange={setSmsOptIn} testid="signup-sms-optin" />
        <Input label="Password" name="password" type="password" value={form.password} onChange={onChange} placeholder="At least 8 characters" testid="signup-password" autoComplete="new-password" />
        {role === "driver" && (
          <div>
            <label className="block text-[11px] uppercase tracking-widest font-bold text-zinc-400 mb-2">
              Referral code <span className="text-zinc-600 normal-case tracking-normal">(optional)</span>
            </label>
            <input
              name="referral_code"
              type="text"
              value={form.referral_code}
              onChange={(e) => setForm((f) => ({ ...f, referral_code: e.target.value.toUpperCase() }))}
              placeholder="6-character code"
              maxLength={12}
              data-testid="signup-referral"
              className="w-full bg-[#141416] border border-white/[0.08] focus:border-[#FF3B30] focus:outline-none text-white placeholder-zinc-600 p-4 uppercase tracking-widest font-bold rounded-none transition-colors"
            />
          </div>
        )}
        {role === "driver" ? (
          <div className="border border-[#FF3B30]/40 bg-[#FF3B30]/[0.06] p-4 text-center" data-testid="driver-gate-notice">
            <p className="text-xs text-zinc-300 mb-3 leading-relaxed">
              Drivers register with live DOT verification and secure document upload — same 90 seconds, plus FMCSA peace of mind.
            </p>
            <Link
              to="/driver-signup"
              data-testid="goto-driver-signup"
              className="inline-block bg-[#FF3B30] hover:bg-[#ff5044] text-white font-bold text-sm uppercase tracking-widest px-6 py-3 transition-colors"
            >
              Continue to driver registration →
            </Link>
          </div>
        ) : (
          <>
            <TosCheckbox checked={tosAccepted} onChange={setTosAccepted} testid="signup-tos" />
            <SubmitButton loading={submitting} label="Create account" testid="signup-submit" />
          </>
        )}
      </form>
      <p className="text-xs text-zinc-500 mt-6 text-center">
        Have an account?{" "}
        <Link to="/login" className="text-[#FF3B30] hover:text-white underline underline-offset-2">
          Sign in
        </Link>
      </p>
    </AuthShell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [submitting, setSubmitting] = useState(false);
  const { refresh } = useAuth();
  const nav = useNavigate();

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (form.password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    if (form.password !== form.confirm) {
      toast.error("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, new_password: form.password });
      setToken(data.token); // auto-login
      await refresh();
      toast.success("Password updated — you're signed in.");
      const role = data.user?.role;
      nav(role === "driver" ? "/app/driver" : role === "admin" ? "/app/admin/users" : "/app", { replace: true });
    } catch (err) {
      toast.error(apiErrorMessage(err, "Reset failed — request a fresh link."));
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <AuthShell title="Reset password" subtitle="This link is missing its token.">
        <p className="text-sm text-zinc-400" data-testid="reset-invalid">
          Request a fresh reset link from the sign-in page.
        </p>
        <p className="text-xs text-zinc-500 mt-6 text-center">
          <Link to="/login" className="text-[#FF3B30] hover:text-white underline underline-offset-2">
            Back to sign in
          </Link>
        </p>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Reset password" subtitle="Pick a new one — reset links expire 15 minutes after they're sent.">
      <form onSubmit={onSubmit} className="space-y-4" data-testid="reset-password-form" noValidate>
        <Input label="New password" name="password" type="password" value={form.password} onChange={onChange} placeholder="At least 8 characters" testid="reset-password" autoComplete="new-password" />
        <Input label="Confirm new password" name="confirm" type="password" value={form.confirm} onChange={onChange} placeholder="Same as above" testid="reset-password-confirm" autoComplete="new-password" />
        <SubmitButton loading={submitting} label="Reset & sign in" testid="reset-submit" />
      </form>
    </AuthShell>
  );
}

function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6 py-16 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-10 left-10 w-[420px] h-[420px] bg-[#FF3B30]/[0.08] blur-[100px]" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-[#FF3B30]/[0.04] blur-[120px]" />
      </div>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-md bg-[#0F0F10] border border-white/[0.08] p-8 md:p-10"
      >
        <Link to="/" className="inline-flex items-center gap-3 mb-8">
          <LogoMark size={32} />
          <Wordmark className="text-lg" />
        </Link>
        <h1 className="font-display text-3xl md:text-4xl font-black uppercase leading-tight">{title}</h1>
        <p className="text-sm text-zinc-500 mt-2 mb-8">{subtitle}</p>
        {children}
      </motion.div>
    </div>
  );
}

function Input({ label, name, type = "text", value, onChange, placeholder, testid, autoComplete, withToggle = false }) {
  const [show, setShow] = useState(false);
  const toggled = type === "password" && withToggle;
  return (
    <div>
      <label htmlFor={name} className="block text-[11px] uppercase tracking-widest font-bold text-zinc-400 mb-2">
        {label}
      </label>
      <div className="relative">
        <input
          id={name}
          name={name}
          type={toggled && show ? "text" : type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          autoComplete={autoComplete}
          required
          data-testid={testid}
          className={`w-full bg-[#141416] border border-white/[0.08] focus:border-[#FF3B30] focus:outline-none text-white placeholder-zinc-600 p-4 rounded-none transition-colors${toggled ? " pr-12" : ""}`}
        />
        {toggled && (
          <button
            type="button"
            onClick={() => setShow((s) => !s)}
            data-testid={`${testid}-toggle`}
            aria-label={show ? "Hide password" : "Show password"}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white transition-colors"
          >
            {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        )}
      </div>
    </div>
  );
}

function SubmitButton({ loading, label, testid }) {
  return (
    <button
      type="submit"
      disabled={loading}
      data-testid={testid}
      className="group w-full bg-[#FF3B30] hover:bg-[#D62828] disabled:opacity-60 text-white font-bold uppercase tracking-widest border-2 border-[#FF3B30] hover:border-[#D62828] py-4 px-6 flex items-center justify-center gap-3 transition-all"
    >
      {loading ? "Working…" : label}
      <ArrowRight className={`w-4 h-4 transition-transform ${loading ? "" : "group-hover:translate-x-1"}`} />
    </button>
  );
}
